<template>
  <div class="public-orc-page" :style="pageStyle">
    <div v-if="loading" class="public-orc-state">
      <div class="orc-list-loading__spinner" />
      <p>{{ t('orcamentos.loading') }}</p>
    </div>

    <div v-else-if="error" class="public-orc-state public-orc-state--error">
      <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
      <p>{{ error }}</p>
    </div>

    <template v-else>
      <header class="public-orc-header" :style="headerStyle">
        <div class="public-orc-header__brand">
          <div class="public-orc-header__logo" :style="logoStyle">
            <img
              v-if="workspacePhotoUrl"
              :src="workspacePhotoUrl"
              :alt="workspaceName"
              class="public-orc-header__logo-img"
            />
            <span v-else>{{ headerInitials }}</span>
          </div>
          <div>
            <strong>{{ headerTitle }}</strong>
            <span v-if="meta?.numero" :style="{ color: accentColor }">{{ meta.numero }}</span>
          </div>
        </div>
        <div class="public-orc-header__actions">
          <button type="button" class="public-orc-btn public-orc-btn--whatsapp" @click="shareWhatsapp">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            {{ t('orcamentos.public.shareWhatsapp') }}
          </button>
          <button type="button" class="public-orc-btn public-orc-btn--pdf" :disabled="pdfLoading" @click="downloadPdf">
            <svg v-if="!pdfLoading" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            <span v-else class="orc-config-save__spinner" />
            {{ t('orcamentos.public.downloadPdf') }}
          </button>
        </div>
      </header>

      <div class="public-orc-viewport">
        <div class="public-orc-paper" :style="paperStyle">
          <iframe
            ref="docFrame"
            class="public-orc-frame"
            :srcdoc="htmlContent"
            title="Orçamento"
            @load="resizeFrame"
          />
        </div>
      </div>

      <footer class="public-orc-footer">
        <span>{{ t('orcamentos.public.footerHint') }}</span>
      </footer>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import publicApi from '@/services/publicApi'
import { resolveMediaUrl } from '@/config/app'
import { whatsappShareUrl, patchOrcamentoHtmlMedia, resolveOrcamentoMediaUrl } from '@/utils/orcamentoUtils'
import { exportOrcamentoPdfFromFullHtml } from '@/utils/orcamentoPdf'
import { adjustColorLightness, getWorkspaceAccentStyle } from '@/utils/workspaceColor'
import '@/assets/styles/workspace-orcamentos.css'

const route = useRoute()
const { t } = useI18n()

const loading = ref(true)
const pdfLoading = ref(false)
const error = ref('')
const htmlContent = ref('')
const docFrame = ref(null)
const meta = ref(null)

const accentColor = computed(() => meta.value?.workspace?.corWorkspace?.trim() || '#5865F2')

const workspaceName = computed(() => meta.value?.workspace?.nome || '')

const workspacePhotoUrl = computed(() => {
  const raw = meta.value?.workspace?.fotoWorkspace
  if (!raw) return ''
  return resolveOrcamentoMediaUrl(raw) || resolveMediaUrl(raw) || raw
})

function applyHtmlContent (data) {
  const raw = data?.htmlContent || ''
  const foto = data?.workspace?.fotoWorkspace || ''
  htmlContent.value = patchOrcamentoHtmlMedia(raw, foto)
}

const headerTitle = computed(() =>
  meta.value?.empresa?.nome?.trim()
  || workspaceName.value
  || meta.value?.titulo
  || t('orcamentos.config.previewQuoteLabel')
)

const headerInitials = computed(() => {
  const name = workspaceName.value || meta.value?.empresa?.nome || meta.value?.titulo || ''
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return 'OR'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
})

const pageGradientTop = computed(() => adjustColorLightness(accentColor.value, 42))

const pageStyle = computed(() => ({
  ...getWorkspaceAccentStyle(accentColor.value),
  '--public-orc-shell-bg': pageGradientTop.value,
  background: `linear-gradient(180deg, ${pageGradientTop.value} 0%, #f1f5f9 42%, #f8fafc 100%)`
}))

const headerStyle = computed(() => {
  const accent = accentColor.value
  return {
    '--public-orc-accent': accent,
    '--public-orc-accent-soft': adjustColorLightness(accent, 38),
    '--public-orc-accent-muted': `${accent}1f`,
    '--public-orc-accent-border': `${accent}40`,
    '--public-orc-accent-glow': `${accent}18`
  }
})

const logoStyle = computed(() => ({
  background: workspacePhotoUrl.value
    ? '#fff'
    : `linear-gradient(135deg, ${accentColor.value} 0%, ${adjustColorLightness(accentColor.value, -14)} 100%)`,
  boxShadow: `0 4px 14px ${accentColor.value}59`
}))

const paperStyle = computed(() => ({
  background: `linear-gradient(180deg, ${adjustColorLightness(accentColor.value, 38)} 0%, #f1f5f9 100%)`
}))

function resizeFrame () {
  const iframe = docFrame.value
  if (!iframe?.contentDocument) return
  const doc = iframe.contentDocument
  const h = Math.max(
    doc.documentElement?.scrollHeight || 0,
    doc.body?.scrollHeight || 0,
    640
  )
  iframe.style.height = `${h}px`
}

async function load () {
  loading.value = true
  error.value = ''
  try {
    const { data } = await publicApi.get(`/public/orcamentos/${route.params.token}`)
    meta.value = data
    applyHtmlContent(data)
    if (!htmlContent.value) {
      error.value = t('orcamentos.public.notFound')
    }
  } catch (e) {
    error.value = e?.response?.data?.error || t('orcamentos.public.notFound')
  } finally {
    loading.value = false
  }
}

function shareWhatsapp () {
  const url = window.location.href
  const label = meta.value?.titulo || meta.value?.numero || t('orcamentos.config.previewQuoteLabel')
  const text = `${t('orcamentos.public.shareText')} ${label}\n${url}`
  window.open(whatsappShareUrl(text), '_blank', 'noopener')
}

async function downloadPdf () {
  if (!htmlContent.value || pdfLoading.value) return
  pdfLoading.value = true
  try {
    const name = `orcamento-${meta.value?.numero || route.params.token}.pdf`
    await exportOrcamentoPdfFromFullHtml(
      htmlContent.value,
      name,
      meta.value?.workspace?.fotoWorkspace || ''
    )
  } finally {
    pdfLoading.value = false
  }
}

function syncPublicOrcShellBg () {
  const top = pageGradientTop.value
  document.documentElement.style.setProperty('--public-orc-shell-bg', top)
  document.body.style.setProperty('--public-orc-shell-bg', top)
}

onMounted(() => {
  document.body.classList.add('public-orc-route')
  syncPublicOrcShellBg()
  load()
})

onUnmounted(() => {
  document.body.classList.remove('public-orc-route')
  document.documentElement.style.removeProperty('--public-orc-shell-bg')
  document.body.style.removeProperty('--public-orc-shell-bg')
})

watch(pageGradientTop, syncPublicOrcShellBg)
</script>
